import { useState, useEffect, useRef } from 'react';
import { useBLSContext } from '../context/BLSContext';
import { useBLSAudio } from '../hooks/useBLSAudio';
import { useZoomApp } from '../hooks/useZoomApp';
import { BLSVisual } from './BLSVisual';
import { StopButton } from './StopButton';
import { SettingsMenu } from './settings/SettingsMenu';
import { speedToHz } from './SpeedControl';
import { getMixedModeEngine } from '../utils/mixedModeEngine';
import type { SoundName } from '../types';
import { COLOR_PRESETS, PRO_FEATURES, BACKGROUND_COLORS, SOUND_LIBRARY } from '../types';

/**
 * ClientView - Fullscreen BLS experience for participants
 *
 * Features:
 * - Fullscreen visual stimulation
 * - Bilateral audio
 * - Emergency stop button
 * - Auto-start from therapist commands
 * - Passive experience (no controls)
 */

export function ClientView(): JSX.Element {
  const { state, dispatch } = useBLSContext();
  const audio = useBLSAudio();
  const { onMessage, sendMessage, sendToOtherInstances, isReady, expandToFullscreen, sendAppToBackground, bringAppToFront, collapseToSidebar, runningContext } = useZoomApp();
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [shouldEnterImmersive, setShouldEnterImmersive] = useState(false);

  // Extract updatePan callback for audio synchronization
  const handlePanUpdate = audio.updatePan;

  // Detect if we're rendering in immersive mode vs sidebar
  const isInImmersive = runningContext === 'inImmersive';

  // Announce connection to therapist when client app opens
  useEffect(() => {
    if (isReady && !isInImmersive) {
      console.log('[CLIENT] Announcing connection to therapist');
      sendMessage({ action: 'client_connected' }).catch((err) => {
        console.warn('[CLIENT] Failed to announce connection:', err);
      });

      // Cleanup: Announce disconnection when component unmounts
      return () => {
        console.log('[CLIENT] Announcing disconnection to therapist');
        sendMessage({ action: 'client_disconnected' }).catch((err) => {
          console.warn('[CLIENT] Failed to announce disconnection:', err);
        });
      };
    }
  }, [isReady, isInImmersive, sendMessage]);

  // When immersive instance mounts, request current state from sidebar
  useEffect(() => {
    if (isInImmersive && isReady) {
      console.log('[IMMERSIVE] Mounted - requesting current state from sidebar');
      sendToOtherInstances({
        action: 'request_state',
      }).catch((err) => {
        console.warn('[IMMERSIVE] Failed to request state from sidebar:', err);
      });
    }
  }, [isInImmersive, isReady, sendToOtherInstances]);

  // Auto-start audio when files finish loading (immersive mode only)
  // This handles the case where update_state arrives before audio files are loaded
  useEffect(() => {
    if (isInImmersive && audio.isLoaded && state.isPlaying && !audio.isPlaying) {
      if (state.audioEnabled !== false) {
        console.log('[IMMERSIVE] Audio loaded, auto-starting with sounds:', state.selectedSounds);
        audio.start(state.selectedSounds, speedToHz(state.speed));
        audio.setVolume(state.volume);
      }
    }
  }, [isInImmersive, audio.isLoaded, state.isPlaying, state.selectedSounds, state.speed, state.volume, state.audioEnabled, audio]);

  // Listen for messages from therapist
  // Use refs to access latest values without re-subscribing
  const audioRef = useRef(audio);
  const stateRef = useRef(state);
  const runningContextRef = useRef(runningContext);
  const mixedModeEngineRef = useRef(getMixedModeEngine());

  useEffect(() => {
    audioRef.current = audio;
    stateRef.current = state;
    runningContextRef.current = runningContext;
  });

  useEffect(() => {
    console.log(`[${runningContext}] Setting up message listener, isReady:`, isReady);
    if (!isReady) return;

    const unsubscribe = onMessage((message) => {
      const currentContext = runningContextRef.current || 'inMeeting';
      console.log(`[${currentContext}] Client received message:`, message);

      // Forward update_state messages from sidebar to immersive
      if (currentContext === 'inMeeting' && message.action === 'update_state') {
        console.log('[SIDEBAR] Forwarding update_state to immersive via postMessage');
        sendToOtherInstances(message).catch((err) => {
          console.warn('[SIDEBAR] Failed to forward (immersive may not exist yet):', err);
        });
      }

      // Respond to state request from immersive instance (for initial state sync)
      if (currentContext === 'inMeeting' && message.action === 'request_state') {
        console.log('[SIDEBAR] Immersive requested state, sending current state:', stateRef.current);
        sendToOtherInstances({
          action: 'update_state',
          params: stateRef.current,
        }).catch((err) => {
          console.warn('[SIDEBAR] Failed to send state to immersive:', err);
        });
      }

      switch (message.action) {
        case 'start_bls':
          if (message.params) {
            console.log(`[${currentContext}] Starting BLS with params:`, message.params);

            // Update state with therapist's settings
            dispatch({ type: 'SET_STATE', state: message.params });

            // Capture params in closure
            const params = message.params;

            // Start audio and visual (only in sidebar, not in immersive)
            if (currentContext === 'inMeeting') {
              setTimeout(async () => {
                // If visual is disabled (audio-only mode), load sounds first if needed
                if (params.visualEnabled === false && !audioRef.current.isLoaded) {
                  const sounds = params.selectedSounds || ['beep'];
                  console.log('[SIDEBAR] Audio-only mode: priority loading sounds:', sounds);
                  await audioRef.current.loadPrioritySounds(sounds);
                }

                // For audio-only mode: Start animation BEFORE audio (animation provides pan updates)
                if (params.visualEnabled === false) {
                  // Dispatch START first to trigger animation render
                  dispatch({ type: 'START' });
                  // Wait for animation to start generating pan updates
                  await new Promise(resolve => setTimeout(resolve, 100));

                  // Now start audio (animation is already sending pan updates)
                  if (params.audioEnabled !== false) {
                    const sounds = params.selectedSounds || ['beep'];
                    audioRef.current.start(sounds, speedToHz(params.speed || 5));
                    if (params.volume !== undefined) {
                      audioRef.current.setVolume(params.volume);
                    }
                  }
                } else {
                  // For visual mode: original order (audio, then dispatch)
                  // Start audio only if audioEnabled
                  if (params.audioEnabled !== false) {
                    const sounds = params.selectedSounds || ['beep'];
                    audioRef.current.start(sounds, speedToHz(params.speed || 5));
                    if (params.volume !== undefined) {
                      audioRef.current.setVolume(params.volume);
                    }
                  }
                  dispatch({ type: 'START' });
                }

                // Only enter immersive mode if visual is enabled
                if (params.visualEnabled !== false) {
                  setIsFullscreen(true);
                  // Trigger immersive mode after React renders
                  setShouldEnterImmersive(true);
                }
                // If visual is disabled, stay in sidebar for audio-only mode
              }, 100);
            }
          }
          break;

        case 'stop_bls':
          audioRef.current.stop();
          dispatch({ type: 'STOP' });
          setIsFullscreen(false);
          setShouldEnterImmersive(false);
          // Exit immersive mode and show sidebar
          collapseToSidebar();
          bringAppToFront();
          break;

        case 'update_state':
          if (message.params) {
            // Capture params and current state BEFORE dispatch
            const params = message.params;
            const isImmersive = currentContext === 'inImmersive';

            // Update state
            dispatch({ type: 'SET_STATE', state: message.params });

            // Merge params with current state to get the new state
            const newState = { ...stateRef.current, ...params };

            // IMMERSIVE: Priority load selected sounds on first state sync
            if (isImmersive && newState.isPlaying && !audioRef.current.isLoaded) {
              const sounds = newState.selectedSounds || ['tap'];
              console.log('[IMMERSIVE] Received initial state, priority loading:', sounds);
              audioRef.current.loadPrioritySounds(sounds);
              // Don't start audio here - auto-start effect will handle it when loaded
            }

            // For immersive: Handle audio and visual toggles during session
            if (isImmersive && newState.isPlaying) {
              const audioIsPlaying = audioRef.current.isPlaying;

              // Handle audioEnabled toggle
              if (params.audioEnabled !== undefined) {
                if (params.audioEnabled) {
                  // Re-enable: start audio
                  const sounds = newState.selectedSounds || ['beep'];
                  console.log('[IMMERSIVE] Audio re-enabled, starting:', sounds);
                  audioRef.current.start(sounds, speedToHz(newState.speed));
                  audioRef.current.setVolume(newState.volume);
                } else {
                  // Disable: stop audio
                  console.log('[IMMERSIVE] Audio disabled, stopping');
                  audioRef.current.stop();
                }
              }
              // Handle sounds change incrementally (without stopping other sounds)
              else if (params.selectedSounds !== undefined && audioIsPlaying) {
                const oldSounds = stateRef.current.selectedSounds || [];
                const newSounds = params.selectedSounds || [];

                // Find removed and added sounds
                const removedSounds = oldSounds.filter((s: SoundName) => !newSounds.includes(s));
                const addedSounds = newSounds.filter((s: SoundName) => !oldSounds.includes(s));

                console.log('[IMMERSIVE] Incremental sound update - removed:', removedSounds, 'added:', addedSounds);

                // Remove sounds that are no longer selected
                for (const sound of removedSounds) {
                  audioRef.current.removeSound(sound);
                }
                // Add newly selected sounds
                for (const sound of addedSounds) {
                  audioRef.current.addSound(sound);
                }
              }
              // Start audio if not currently playing
              else if (!audioIsPlaying) {
                if (newState.audioEnabled !== false) {
                  const sounds = newState.selectedSounds || ['beep'];
                  audioRef.current.start(sounds, speedToHz(newState.speed));
                  audioRef.current.setVolume(newState.volume);
                }
              }
              // Update volume if it changed
              else if (params.volume !== undefined) {
                audioRef.current.setVolume(params.volume);
              }

              // Handle visualEnabled toggle (no action needed - React will re-render)
              if (params.visualEnabled !== undefined) {
                console.log('[IMMERSIVE] Visual toggled to:', params.visualEnabled);
                // State already updated via dispatch above, React will re-render
                // and show/hide visual + audio-only message automatically
              }
            }
            // For sidebar: Update audio if already playing
            else if (!isImmersive && newState.isPlaying) {
              // Handle visualEnabled toggle: OFF → ON (enter immersive mode)
              if (params.visualEnabled === true && stateRef.current.visualEnabled === false) {
                console.log('[SIDEBAR] Visual enabled, entering immersive mode...');
                setIsFullscreen(true);
                setShouldEnterImmersive(true);
                // Audio continues playing, immersive mode will handle it
                return; // Don't process other updates, immersive will take over
              }

              // Handle audioEnabled toggle
              if (params.audioEnabled !== undefined) {
                if (params.audioEnabled) {
                  // Re-enable: start audio
                  const sounds = newState.selectedSounds || ['beep'];
                  audioRef.current.start(sounds, speedToHz(newState.speed));
                  audioRef.current.setVolume(newState.volume);
                } else {
                  // Disable: stop audio
                  audioRef.current.stop();
                }
              }
              // Handle sounds change incrementally (without stopping other sounds)
              else if (params.selectedSounds !== undefined && audioRef.current.isPlaying) {
                const oldSounds = stateRef.current.selectedSounds || [];
                const newSounds = params.selectedSounds || [];

                // Find removed and added sounds
                const removedSounds = oldSounds.filter((s: SoundName) => !newSounds.includes(s));
                const addedSounds = newSounds.filter((s: SoundName) => !oldSounds.includes(s));

                console.log('[SIDEBAR] Incremental sound update - removed:', removedSounds, 'added:', addedSounds);

                // Remove sounds that are no longer selected
                for (const sound of removedSounds) {
                  audioRef.current.removeSound(sound);
                }
                // Add newly selected sounds
                for (const sound of addedSounds) {
                  audioRef.current.addSound(sound);
                }
              }
              // Handle speed change (requires restart for continuous sounds)
              else if (params.speed !== undefined) {
                const audioEnabled = newState.audioEnabled !== false;
                if (audioEnabled) {
                  audioRef.current.stop();
                  setTimeout(() => {
                    const sounds = newState.selectedSounds || ['beep'];
                    audioRef.current.start(sounds, speedToHz(params.speed || newState.speed));
                  }, 50);
                }
              }
              if (params.volume !== undefined) {
                audioRef.current.setVolume(params.volume);
              }
            }
          }
          break;
      }
    });

    return unsubscribe;
  }, [isReady, onMessage, dispatch, collapseToSidebar, bringAppToFront]);

  // Mixed Mode: Client-side sync
  // CRITICAL: Only re-run when isPlaying, mixedMode, or mixedModeConfig changes
  useEffect(() => {
    const mixedEngine = mixedModeEngineRef.current;
    const audioEngine = audioRef.current;

    // Check if ANY randomization is enabled (either All Random or individual)
    const isAnyRandomizationEnabled =
      state.mixedMode ||
      Object.values(state.mixedModeConfig).some(v => v === true);

    if (state.isPlaying && isAnyRandomizationEnabled) {
      const proColors = COLOR_PRESETS.filter(c => c.isPro).map(c => c.value);
      const proBackgrounds = BACKGROUND_COLORS.filter(b => b.isPro).map(b => b.value);

      // Get all beep sounds (not continuous sounds) for randomization
      const allBeepSounds: SoundName[] = (Object.keys(SOUND_LIBRARY) as SoundName[])
        .filter(soundName => {
          return soundName !== 'ocean' && soundName !== 'ambient' && soundName !== 'angelic'
                 && soundName !== 'handpan' && soundName !== 'underwater' && soundName !== 'drone';
        });

      mixedEngine.start({
        availableColors: proColors,
        availableDirections: PRO_FEATURES.directions,
        availableMovementStyles: PRO_FEATURES.movementStyles,
        availableBackgrounds: proBackgrounds,
        availableShapes: PRO_FEATURES.shapes,
        availableSounds: allBeepSounds,
        randomizationConfig: state.mixedModeConfig,
        onColorChange: (color) => dispatch({ type: 'SET_COLOR', color }),
        onDirectionChange: (direction) => dispatch({ type: 'SET_DIRECTION', direction }),
        onMovementStyleChange: (style) => dispatch({ type: 'SET_MOVEMENT_STYLE', style }),
        onBackgroundChange: (background) => dispatch({ type: 'SET_BACKGROUND', background }),
        onShapeChange: (shape) => dispatch({ type: 'SET_SHAPE', shape }),
      });

      // Only enable audio mixed mode if randomizeSounds is true
      if (state.mixedModeConfig.randomizeSounds) {
        audioEngine.enableMixedMode(allBeepSounds);
      }
    } else {
      mixedEngine.stop();
      audioEngine.disableMixedMode();
    }

    return () => mixedEngine.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isPlaying, state.mixedMode, state.mixedModeConfig]);

  // Enter immersive mode AFTER React renders the BLS view
  useEffect(() => {
    if (shouldEnterImmersive && isFullscreen) {
      console.log('React rendered BLS view, now entering immersive mode...');
      expandToFullscreen();
      sendAppToBackground();
      setShouldEnterImmersive(false); // Reset flag
    }
  }, [shouldEnterImmersive, isFullscreen, expandToFullscreen, sendAppToBackground]);

  const handleStop = () => {
    audio.stop();
    dispatch({ type: 'STOP' });
    setIsFullscreen(false);

    // Exit immersive mode and show sidebar
    collapseToSidebar();
    bringAppToFront();

    // Notify therapist
    sendMessage({ action: 'client_stopped' }).catch((error) => {
      console.error('Failed to notify therapist:', error);
    });
  };

  // If we're in immersive mode, ALWAYS show BLS
  // (we only enter immersive when BLS is active)
  if (isInImmersive) {
    console.log('[IMMERSIVE] Rendering BLS with state:', {
      isFullscreen,
      isPlaying: state.isPlaying,
      color: state.color,
      direction: state.direction,
      speed: state.speed,
      visualEnabled: state.visualEnabled,
      audioEnabled: state.audioEnabled,
      runningContext,
    });

    // Show fullscreen BLS in immersive mode
    return (
      <div
        className="relative w-screen h-screen overflow-hidden flex items-center justify-center"
        style={{ backgroundColor: state.background }}
      >
        {/* BLS Visual - fullscreen (when visual enabled) */}
        {state.visualEnabled && (
          <BLSVisual
            color={state.color}
            direction={state.direction}
            speed={state.speed}
            isPlaying={true}
            movementStyle={state.movementStyle}
            shape={state.shape}
            onPanUpdate={handlePanUpdate}
          />
        )}

        {/* Invisible animation for audio-only mode (provides pan updates) */}
        {!state.visualEnabled && state.audioEnabled && (
          <div className="absolute inset-0 opacity-0 pointer-events-none">
            <BLSVisual
              color={state.color}
              direction={state.direction}
              speed={state.speed}
              isPlaying={true}
              movementStyle={state.movementStyle}
              shape={state.shape}
              onPanUpdate={handlePanUpdate}
            />
          </div>
        )}

        {/* Audio-only message (when visual disabled) */}
        {!state.visualEnabled && state.audioEnabled && (
          <div className="text-center space-y-4 p-8 z-10">
            <div className="text-8xl mb-4">🎧</div>
            <h1 className="text-4xl font-bold text-gray-900">
              Audio BLS Active
            </h1>
            <p className="text-gray-600 text-xl">
              Bilateral audio stimulation in progress
            </p>

            {/* Audio indicator */}
            <div className="flex items-center justify-center gap-2 mt-8 bg-white rounded-full px-6 py-3 shadow-lg">
              <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-base font-medium text-gray-700">Playing</span>
            </div>
          </div>
        )}

        {/* Session paused message (when both disabled) */}
        {!state.visualEnabled && !state.audioEnabled && (
          <div className="text-center space-y-4 p-8 z-10">
            <div className="text-8xl mb-4">⏸️</div>
            <h1 className="text-4xl font-bold text-gray-900">
              Session Paused
            </h1>
            <p className="text-gray-600 text-xl">
              Waiting for the therapist to resume...
            </p>
          </div>
        )}

        {/* Emergency stop button */}
        <StopButton onStop={handleStop} />
      </div>
    );
  }

  // Sidebar view - playing mode (visual or audio-only)
  if (!isFullscreen && state.isPlaying) {
    return (
      <div className="relative flex flex-col items-center justify-center h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        {/* Visual + Animation (visible when visual enabled) */}
        {state.visualEnabled && (
          <div className="absolute inset-0 flex items-center justify-center">
            <BLSVisual
              color={state.color}
              direction={state.direction}
              speed={state.speed}
              isPlaying={true}
              movementStyle={state.movementStyle}
              shape={state.shape}
              onPanUpdate={handlePanUpdate}
            />
          </div>
        )}

        {/* Invisible animation for audio-only mode (provides pan updates) */}
        {!state.visualEnabled && state.audioEnabled && (
          <div className="absolute inset-0 opacity-0 pointer-events-none">
            <BLSVisual
              color={state.color}
              direction={state.direction}
              speed={state.speed}
              isPlaying={true}
              movementStyle={state.movementStyle}
              shape={state.shape}
              onPanUpdate={handlePanUpdate}
            />
          </div>
        )}

        {/* Audio-only message (when visual disabled) */}
        {!state.visualEnabled && state.audioEnabled && (
          <div className="text-center space-y-4 p-8 z-10">
            <div className="text-6xl mb-2">🎧</div>
            <h1 className="text-2xl font-bold text-gray-900">
              Audio Session Active
            </h1>
            <p className="text-gray-600">
              Bilateral audio stimulation in progress
            </p>

            {/* Audio indicator */}
            <div className="flex items-center justify-center gap-2 mt-6 bg-white rounded-full px-4 py-2 shadow-md">
              <div className="h-2.5 w-2.5 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-gray-700">Playing</span>
            </div>
          </div>
        )}

        {/* Session paused message (when both disabled in sidebar) */}
        {!state.visualEnabled && !state.audioEnabled && (
          <div className="text-center space-y-4 p-8 z-10">
            <div className="text-6xl mb-2">⏸️</div>
            <h1 className="text-2xl font-bold text-gray-900">
              Session Paused
            </h1>
            <p className="text-gray-600">
              Waiting the for therapist to resume...
            </p>
          </div>
        )}

        {/* Stop button */}
        <StopButton onStop={handleStop} />
      </div>
    );
  }

  // Sidebar view - waiting screen when not playing
  if (!isFullscreen) {
    return (
      <div className="relative flex flex-col items-center justify-center h-screen bg-app-bg">
        {/* Settings button in top-right corner */}
        <div className="absolute top-4 right-4">
          <SettingsMenu />
        </div>

        <div className="text-center space-y-6 p-8">
          <img
            src="/assets/logos/transparent-text.png"
            alt="EMDR Sync Logo"
            className="w-48 mx-auto"
          />
          <p className="text-text-muted text-lg">
            Waiting for the therapist to start bilateral stimulation...
          </p>

          {!isReady && (
            <div className="flex items-center justify-center gap-2 text-sm text-text-muted">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-sync-blue"></div>
              <span>Connecting to Zoom...</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Sidebar view - minimal indicator when playing (BLS is in immersive mode)
  return (
    <div className="flex flex-col items-center justify-center h-screen bg-gray-900">
      <div className="text-center space-y-6 p-8">
        <img
          src="/assets/logos/transparent-eye.png"
          alt="EMDR Sync"
          className="w-32 mx-auto"
        />
        <h2 className="text-xl font-bold text-white">
          BLS Session Active
        </h2>
        <p className="text-gray-400 text-sm">
          Fullscreen view active
        </p>
      </div>
    </div>
  );
}
